// The pips: every mark on a badge, and the free actions under it. Folded onto
// whatever badge the piece is wearing, so the marks and the panel behind them
// are computed in one place and cannot describe different pieces.

import {
  type Color,
  KING,
  pieceColor,
  type PieceType,
  pieceType
} from "src/engine/rules/board";
import { plateWorthMarking } from "../plateMark";
import type { GameState } from "src/engine/rules/commands";
import { armourLeft, disguiseLeft, disguisedAs, healthOf, hurtAt, maxHealthOf, pieceAt, platingAt, socksLeft } from "src/engine/rules/position";
import { armyName } from "src/engine/session/armyName";
import {
  canLoadBelt,
  canMine,
  isFoundryAt,
  isMining
} from "src/engine/rules/industry/machines";
import {
  bufferAtLeast,
  bufferTotal,
  cookingCount,
  mineableAt,
  targetAt
} from "src/engine/rules/industry/state";
import { type Despairing, despairView } from "../despairView";
import { PIECE_NAMES, mintCost } from "src/engine/rules/rules";
import { METAL, mintGradeOf } from "src/engine/rules/materials";
import { slotCounts } from "src/engine/session/shelfTotals";
import type { BadgeAction, BadgePip, PieceBadge } from "./types";
import { forgeBlocker } from "./machines";

/**
 * Everything a piece is doing, and everything you can do to it.
 *
 * Computed once per piece and folded onto whatever badge it wears, so the
 * marks and the panel behind them cannot disagree about what is going on.
 */
export function activity(
  state: GameState,
  sq: number,
  owner: Color,
  // **The board marker or the inspector.** A marker is scanned across the
  // whole board, so it carries only news: plate that is worn or extra (every
  // knight and rook is born plated, §31 K1) and never the unset foundry, which
  // since the hotbar forges (U4) is every rook and king. The inspector says
  // both, on the one piece you clicked.
  board = false
): Pick<PieceBadge, "pips" | "materials" | "actions"> {
  const piece = pieceAt(state.position, sq);
  const type = pieceType(piece);
  const pips: BadgePip[] = [];

  // **The army's own countdown, on the piece it happens to.** The banner says
  // an army is in a coma; it does not say *which* king, and on a board with
  // three armies and a camp arriving that is the whole question. It goes first
  // because it outranks anything the piece is busy with — a foundry saving for
  // a rook it will never build is not the headline.
  const doomed = despairOf(state, sq, type);
  if (doomed) {
    pips.push({
      glyph: "☠",
      label:
        doomed.turnsLeft === 0
          ? "This army is laying down its arms"
          : `This army has no way to win — ${doomed.turnsLeft} ` +
            `${doomed.turnsLeft === 1 ? "turn" : "turns"} to find one`,
      tone: "waiting"
    });
  }

  // **What has happened *to* the piece, before what it is doing.** Whoever owns it, since §26 C4.
  const armour = armourLeft(state.position, sq);
  if (armour > 0) {
    // The expiry's own number: rounds, and a round partly spent is one you still have.
    const rounds = armour;
    pips.push({
      glyph: "⛨",
      label:
        `Frozen — cannot move, take, threaten or be taken, and no lane will ` +
        `carry it. ${rounds} round${rounds === 1 ? "" : "s"} left`,
      tone: "ready"
    });
  }

  // **Programmer Oil, on the king it changes.** A card played turns ago that
  // silently alters what one piece may do is the same failure the lock had:
  // you play it, and then you have to remember. The countdown is the same
  // arithmetic the expiry uses, so the number a player reads is the number
  // that runs out.
  const socks = type === KING ? socksLeft(state.position, owner) : 0;
  if (socks > 0) {
    const turns = socks;
    pips.push({
      glyph: "♛",
      label:
        `Programmer Oil — moves and attacks like a queen. ` +
        `${turns} turn${turns === 1 ? "" : "s"} left`,
      tone: "ready"
    });
  }

  // The token beside the piece says *what* it is dressed as; only the pip can
  // say how long that lasts, which is the Stasis Field's and the Oil's rule.
  const costume = disguisedAs(state.position, sq);
  if (costume) {
    // The armour pip's number: rounds left.
    const rounds = disguiseLeft(state.position, sq);
    pips.push({
      glyph: "🎭",
      label:
        `Flying ${armyName(costume.side)}'s colours as a ` +
        `${PIECE_NAMES[costume.rank].toLowerCase()} — that army and its allies ` +
        `cannot capture it. ${rounds} round${rounds === 1 ? "" : "s"} left, and ` +
        `it comes off the moment this piece takes something of theirs.`,
      tone: "ready"
    });
  }

  // Above the wound: it is what the next shot hits, and the label says what it is not.
  const plate = platingAt(state.position, sq);
  if (plate > 0 && (!board || plateWorthMarking(state.position, sq))) {
    pips.push({
      glyph: "🛡",
      label:
        `Armoured — ${plate} plate. Turret fire and strikes chew through it ` +
        `first; a capture ignores it`,
      tone: "ready"
    });
  }

  const damage = hurtAt(state.position, sq);
  if (damage > 0) {
    const left = healthOf(state.position, sq);
    // `maxHealthOf`, not `PIECE_VALUE`: §27 split the two.
    const whole = maxHealthOf(state.position, sq);
    pips.push({
      glyph: "✚",
      label: `Hurt — ${left} of ${whole} health`,
      tone: "waiting"
    });
  }

  if (isMining(state.position, state.industry, sq)) {
    const left = mineableAt(state.industry, sq);
    // "Here" rather than "in the heap": under a piece it is as likely to be a
    // tile of a field, and a pip that named the wrong thing would be the one
    // place the board says what the ground is.
    pips.push({ glyph: "⛏", label: `Mining — ${left} left here`, tone: "working" });
  }

  const inOven = cookingCount(state.industry, sq);
  if (inOven > 0) {
    pips.push({
      glyph: "♨",
      label: `Refining ${inOven} unit${inOven === 1 ? "" : "s"}`,
      tone: "working"
    });
  }

  if (isFoundryAt(state.position, state.industry, sq)) {
    const target = targetAt(state.industry, sq) as PieceType | 0;
    if (target) {
      const need = mintCost(target);
      const have = bufferAtLeast(
        state.industry,
        sq,
        mintGradeOf(target),
        "in",
        METAL
      );
      // **"Saving for" stops being true the moment it has enough**, and saying
      // it anyway is how a stuck foundry reads as a working one. Reported from
      // a watched game as "saving for a pawn, 6/2" sitting there — which is a
      // foundry that has three times what it needs and *cannot spend it*.
      //
      // So once it can afford the recipe the label says what is stopping it,
      // asked of `canForge` rather than re-derived — the same call the reducer
      // makes, so the badge cannot claim a forge the rules would refuse. The
      // one that persists is `no-room`: every square beside the foundry taken,
      // which a king can now do to itself since `wallsInKing` was retired.
      pips.push({
        glyph: "⚒",
        label:
          have < need
            ? `Saving for a ${PIECE_NAMES[target]} — ${have}/${need}`
            : `${PIECE_NAMES[target]} paid for — ${forgeBlocker(state, sq, owner, target)}`,
        tone: have >= need ? "ready" : "waiting"
      });
    } else if (!board) {
      pips.push({ glyph: "⚒", label: "Foundry with no recipe set", tone: "waiting" });
    }
  }

  const held = bufferTotal(state.industry, sq, "in") + bufferTotal(state.industry, sq, "out");
  if (held > 0 && pips.every((pip) => pip.glyph !== "⛏")) {
    pips.push({ glyph: "▣", label: `Carrying ${held} unit${held === 1 ? "" : "s"}`, tone: "working" });
  }

  // Dearest first: the top one is what the piece is worth, and the list is what
  // it can actually spend. Both banks, since a piece spends out of either.
  const materials = slotCounts(state.industry, sq, ["in", "out"]);

  const actions: BadgeAction[] = [];
  const mining = canMine(state.position, state.industry, sq, owner);
  if (mining === null || mining === "already-mined") {
    actions.push({ kind: "mine", label: "Mine", enabled: mining === null });
  }
  const loading = canLoadBelt(state.position, state.industry, sq, owner);
  if (loading === null || loading === "already-loaded") {
    actions.push({ kind: "load-belt", label: "Hand off", enabled: loading === null });
  }

  return { pips, materials, actions };
}

/**
 * Is this square the king of an army under the diagnosis?
 *
 * Read off `position.despair` through the same view the banner uses, so the
 * number on the pip and the number in the banner are one subtraction rather
 * than two that can disagree. `localSide` is irrelevant here — a pip marks
 * whoever it happens to, and an enemy about to give up is worth seeing.
 */
export function despairOf(state: GameState, sq: number, type: PieceType): Despairing | undefined {
  if (type !== KING) return undefined;
  const owner = pieceColor(pieceAt(state.position, sq));
  // The king's *own* square: a side has one king, and `position.kings` is the
  // authority on where it is.
  if (state.position.kings[owner] !== sq) return undefined;
  return despairView(state, owner).find((entry) => entry.side === owner);
}
